import type { ReactNode } from 'react';

export interface DocItem {
  id: string;
  title: string;
  description: string;
  readingTime: string;
  quickStart: string;
  docUrl: string;
  icon?: string | ReactNode;
}

export const DOC_ITEMS: readonly DocItem[] = [
  {
    id: 'auth',
    title: 'Authentication',
    description:
      'Learn how to authenticate requests to the DPDP Consent API using Bearer tokens and application keys.',
    readingTime: '3 min read',
    quickStart: 'https://docs.dpdp-consent.example.com/quickstart/auth',
    docUrl: 'https://docs.dpdp-consent.example.com/api-reference/authentication',
    icon: '🔑',
  },
  {
    id: 'apps',
    title: 'Applications Management',
    description:
      'Understand the application lifecycle, environment separation (staging vs production), and client isolation.',
    readingTime: '4 min read',
    quickStart: 'https://docs.dpdp-consent.example.com/quickstart/apps',
    docUrl: 'https://docs.dpdp-consent.example.com/guide/applications',
    icon: '📱',
  },
  {
    id: 'consent-api',
    title: 'Consent APIs',
    description:
      'Detailed specifications for notice presentation, consent recording, and ease-of-withdrawal endpoints.',
    readingTime: '5 min read',
    quickStart: 'https://docs.dpdp-consent.example.com/quickstart/consent',
    docUrl: 'https://docs.dpdp-consent.example.com/api-reference/consent',
    icon: '📝',
  },
  {
    id: 'widget',
    title: 'Consent Widget',
    description:
      'Embed the lightweight vanilla JavaScript consent widget via script tag or npm bundle using Shadow DOM.',
    readingTime: '6 min read',
    quickStart: 'https://docs.dpdp-consent.example.com/quickstart/widget',
    docUrl: 'https://docs.dpdp-consent.example.com/guide/widget-integration',
    icon: '⚙️',
  },
  {
    id: 'sdk',
    title: 'Node.js & JS SDKs',
    description:
      'Quickly integrate the DPDP Consent APIs using our official pre-compiled client packages.',
    readingTime: '3 min read',
    quickStart: 'https://docs.dpdp-consent.example.com/quickstart/sdk',
    docUrl: 'https://docs.dpdp-consent.example.com/guide/sdk',
    icon: '📦',
  },
  {
    id: 'webhooks',
    title: 'Webhooks Synchronization',
    description:
      'Configure active webhook endpoints to receive downstream status updates on consent and withdrawals.',
    readingTime: '4 min read',
    quickStart: 'https://docs.dpdp-consent.example.com/quickstart/webhooks',
    docUrl: 'https://docs.dpdp-consent.example.com/guide/webhooks',
    icon: '🔔',
  },
];

interface DocShortcutsProps {
  limit?: number;
}

export function DocShortcuts({ limit }: DocShortcutsProps) {
  const items = limit ? DOC_ITEMS.slice(0, limit) : DOC_ITEMS;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {items.map((item) => (
        <div
          key={item.id}
          className="rounded-2xl border border-slate-900 bg-slate-900/20 p-6 flex flex-col justify-between hover:border-slate-800 transition duration-200"
        >
          <div>
            <div className="flex items-center justify-between">
              <span className="text-2xl" role="img" aria-hidden="true">
                {item.icon}
              </span>
              <span className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">
                {item.readingTime}
              </span>
            </div>
            <h3 className="mt-4 text-base font-bold text-white">{item.title}</h3>
            <p className="mt-2 text-xs text-slate-400 leading-relaxed">{item.description}</p>
          </div>
          <div className="mt-6 flex items-center justify-between border-t border-slate-900 pt-4">
            <a
              href={item.quickStart}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] font-semibold text-blue-400 hover:text-blue-300 transition"
            >
              Quick Start →
            </a>
            <a
              href={item.docUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] font-semibold text-slate-400 hover:text-slate-200 transition"
            >
              API Docs ↗
            </a>
          </div>
        </div>
      ))}
    </div>
  );
}
